import { useState, useEffect, useCallback, useRef } from 'react';
import { useViews } from '@/api/views/use-views';
import { View, CreateViewRequest } from '@/models/view';
import { useSnackbar } from 'notistack';
import { usePermissions } from './usePermissions';

/** Normalize filter values so URL string round-trip (e.g. "1" vs 1) doesn't break comparison. */
function normalizeFilterValue(value: unknown): unknown {
    if (value === '' || value === null || value === undefined) return value;
    if (typeof value === 'string' && value === 'true') return true;
    if (typeof value === 'string' && value === 'false') return false;
    if (typeof value === 'string' && /^\d+$/.test(value)) return parseInt(value, 10);
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(value)) {
            out[k] = normalizeFilterValue(v);
        }
        return out;
    }
    return value;
}

function filtersMatch(
    current: Record<string, any>,
    viewFilters: Record<string, any> | undefined
): boolean {
    const a = normalizeFilterValue(current ?? {}) as Record<string, unknown>;
    const b = normalizeFilterValue(viewFilters ?? {}) as Record<string, unknown>;
    return JSON.stringify(a) === JSON.stringify(b);
}

interface UsePageViewsOptions {
    module: 'leads' | 'customers';
    currentFilters: Record<string, any>;
    currentSort: { key: string; direction: 'asc' | 'desc' };
    currentVisibleColumns: string[];
    defaultColumns?: string[];
    /** When set, applyView uses this for one atomic filters+sort+pagination update (avoids URL race). */
    onApplyViewState?: (state: {
        filters: Record<string, any>;
        sort: { key: string; direction: 'asc' | 'desc' } | null;
        pagination: { page: number; pageSize: number };
    }) => void;
    onFiltersChange: (filters: Record<string, any>) => void;
    onSortChange: (sort: { key: string; direction: 'asc' | 'desc' }) => void;
    onVisibleColumnsChange: (columns: string[]) => void;
    currentPageSize?: number;
}

export const usePageViews = ({
    module,
    currentFilters,
    currentSort,
    currentVisibleColumns,
    defaultColumns = [],
    onApplyViewState,
    onFiltersChange,
    onSortChange,
    onVisibleColumnsChange,
    currentPageSize = 25,
}: UsePageViewsOptions) => {
    const { enqueueSnackbar } = useSnackbar();
    const { isAdmin } = usePermissions();
    const { views, isLoading, createView, updateView, deleteView, reorderViews } = useViews(module);
    const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
    const [isSavingView, setIsSavingView] = useState(false);
    const lastAppliedViewIdRef = useRef<string | null>(null);

    // Load default view on mount
    useEffect(() => {
        if (views.length > 0 && !selectedViewId) {
            const defaultView = views.find(v => v.is_default);
            if (defaultView) {
                applyView(defaultView);
                setSelectedViewId(defaultView.id);
            }
        }
    }, [views]);

    // De-select view when user manually changes filters or sort (e.g. "clear all" or editing a filter).
    // Skip one effect run after applying a view so parent state (filters/sort) can update first.
    // Comparison uses normalized values so URL string round-trip (e.g. "1" vs 1) doesn't cause false deselect.
    useEffect(() => {
        if (!selectedViewId) return;
        const view = views.find(v => v.id === selectedViewId);
        if (!view) return;
        if (selectedViewId === lastAppliedViewIdRef.current) {
            lastAppliedViewIdRef.current = null;
            return;
        }
        const sortMatch = !view.sort
            ? true
            : currentSort.key === view.sort.key && currentSort.direction === view.sort.direction;
        if (!filtersMatch(currentFilters, view.filters) || !sortMatch) {
            setSelectedViewId(null);
        }
    }, [selectedViewId, views, currentFilters, currentSort]);

    const applyView = useCallback(
        (view: View) => {
            const filters =
                view.filters && Object.keys(view.filters).length > 0 ? view.filters : {};
            const sort = view.sort ?? null;
            const pagination = { page: 1, pageSize: currentPageSize };

            if (onApplyViewState) {
                onApplyViewState({ filters, sort, pagination });
            } else {
                onFiltersChange(filters);
                if (sort) {
                    onSortChange(sort);
                }
            }

            if (view.visible_columns && view.visible_columns.length > 0) {
                onVisibleColumnsChange(view.visible_columns);
            }
        },
        [onApplyViewState, onFiltersChange, onSortChange, onVisibleColumnsChange, currentPageSize]
    );

    const handleViewSelect = useCallback(
        (viewId: string | null) => {
            setSelectedViewId(viewId);
            if (viewId) {
                lastAppliedViewIdRef.current = viewId;
                const view = views.find(v => v.id === viewId);
                if (view) {
                    applyView(view);
                    enqueueSnackbar(`View "${view.name}" applied`, {
                        variant: 'success',
                        autoHideDuration: 3000,
                    });
                }
            }
        },
        [views, applyView, enqueueSnackbar]
    );

    const handleSaveAsView = useCallback(
        async (name: string, description?: string, isGlobal = false): Promise<View> => {
            if (!name.trim()) {
                enqueueSnackbar('View name is required', { variant: 'error' });
                throw new Error('View name is required');
            }

            if (isGlobal && !isAdmin) {
                enqueueSnackbar('Only administrators can create global views', {
                    variant: 'error',
                });
                throw new Error('Only administrators can create global views');
            }

            setIsSavingView(true);
            try {
                const viewData: CreateViewRequest = {
                    module,
                    name: name.trim(),
                    description: description?.trim(),
                    filters: currentFilters,
                    sort: currentSort,
                    visible_columns: currentVisibleColumns,
                    is_global: isGlobal,
                    order: views.length,
                };

                const newView = await createView.mutateAsync(viewData);
                setSelectedViewId(newView.id);
                enqueueSnackbar('View saved successfully', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
                return newView as View;
            } catch (error: any) {
                enqueueSnackbar(error?.response?.data?.message || 'Failed to save view', {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
                throw error;
            } finally {
                setIsSavingView(false);
            }
        },
        [
            module,
            currentFilters,
            currentSort,
            currentVisibleColumns,
            views.length,
            createView,
            isAdmin,
            enqueueSnackbar,
        ]
    );

    const handleUpdateView = useCallback(
        async (viewId: string, updates: Partial<View>) => {
            try {
                await updateView.mutateAsync({
                    id: viewId,
                    ...updates,
                });
                enqueueSnackbar('View updated successfully', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            } catch (error: any) {
                enqueueSnackbar(error?.response?.data?.message || 'Failed to update view', {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
                throw error;
            }
        },
        [updateView, enqueueSnackbar]
    );

    const handleDeleteView = useCallback(
        async (viewId: string) => {
            try {
                await deleteView.mutateAsync(viewId);
                if (selectedViewId === viewId) {
                    setSelectedViewId(null);
                    // Reset visible columns to default when deleting the currently selected view
                    if (defaultColumns.length > 0) {
                        onVisibleColumnsChange(defaultColumns);
                    }
                }
                enqueueSnackbar('View deleted successfully', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            } catch (error: any) {
                enqueueSnackbar(error?.response?.data?.message || 'Failed to delete view', {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
                throw error;
            }
        },
        [deleteView, selectedViewId, enqueueSnackbar, defaultColumns, onVisibleColumnsChange]
    );

    const selectedView = selectedViewId ? views.find(v => v.id === selectedViewId) : null;

    const handleReorderViews = useCallback(
        async (viewIds: string[]) => {
            try {
                await reorderViews.mutateAsync(viewIds);
                enqueueSnackbar('View order saved', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            } catch (error: any) {
                enqueueSnackbar(error?.response?.data?.message || 'Failed to save view order', {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
                throw error;
            }
        },
        [reorderViews, enqueueSnackbar]
    );

    return {
        views,
        isLoading,
        selectedViewId,
        selectedView,
        handleViewSelect,
        handleSaveAsView,
        handleUpdateView,
        handleDeleteView,
        handleReorderViews,
        isSavingView,
        isAdmin: Boolean(isAdmin),
    };
};
